import { useState } from "react";
import { CERT_MAX_PDF, CERT_MAX_UPLOAD, type CertificateView } from "../../api/certificate";
import { Banner } from "../../components/Banner";
import { FileButton } from "../../components/FileButton";
import { GateFooter } from "../../components/GateFooter";
import { Pill } from "../../components/Pill";
import { ScreenHeader } from "../../components/ScreenHeader";
import { Section } from "../../components/Section";
import { StepChecklist } from "../../components/StepChecklist";
import { shortDate } from "../../lib/format";

interface CertificateScreenProps {
  view: CertificateView;
  uploading: boolean;
  uploadError: string | null;
  busy: boolean;
  onUpload: (file: File) => void;
  onComplete: () => void;
}

/**
 * Step 5 (v2): upload the Growth Pathway certificate. It completes the pathway and opens the Gig Board (and, on the
 * finance track, the handover to the provider).
 */
export function CertificateScreen({
  view,
  uploading,
  uploadError,
  busy,
  onUpload,
  onComplete,
}: CertificateScreenProps) {
  const [fileError, setFileError] = useState<string | null>(null);

  // The same limits the server applies, checked first so a too-big file is caught before it uploads.
  const pick = (file: File) => {
    setFileError(null);
    if (file.size === 0) return setFileError("That file looks empty.");
    const pdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    if (pdf && file.size > CERT_MAX_PDF)
      return setFileError(
        "That PDF is over 2.5 MB. Upload a screenshot or photo of the certificate instead.",
      );
    if (!pdf && !file.type.startsWith("image/"))
      return setFileError("Upload a PDF, or a photo or screenshot of your certificate.");
    if (file.size > CERT_MAX_UPLOAD)
      return setFileError("That file is too large, keep it under 8MB.");
    onUpload(file);
  };

  const step = view.step;
  return (
    <div>
      <ScreenHeader
        kicker="Step 5 · Certificate"
        title="Upload your Growth Pathway certificate."
        sub="This completes the Growth Pathway and opens the Gig Board: real gigs in your skill, refreshed every few hours."
      />
      <StepChecklist gate={view.gate} done={view.done} />
      <Section
        title="Your certificate"
        hint="A PDF (up to 2.5 MB), or a clear photo or screenshot of the certificate from the LMS."
        status={<Pill ok={step.hasCertificate} okText="Uploaded" />}
      >
        <FileButton
          label="Upload my certificate"
          ariaLabel="Upload your Growth Pathway certificate"
          accept="application/pdf,image/*"
          busy={uploading}
          hasFile={step.hasCertificate}
          onFile={pick}
        />
        <p className="mt-2 text-[13px] text-text-muted">
          {uploading
            ? "Uploading..."
            : step.hasCertificate
              ? `✓ ${step.fileName ?? "Your certificate"} uploaded${step.uploadedAt ? ` on ${shortDate(step.uploadedAt)}` : ""}.`
              : "PDF, JPG or PNG."}
        </p>
        {fileError || uploadError ? (
          <p role="alert" className="text-[13px] text-negative">
            {fileError ?? uploadError}
          </p>
        ) : null}
      </Section>
      {view.gate.pass && !view.done ? (
        <Banner tone="good">✓ Certificate uploaded. You can complete the Growth Pathway.</Banner>
      ) : null}
      <GateFooter
        gate={view.gate}
        done={view.done}
        label="Complete the Growth Pathway"
        next={{ to: "/gig/6", label: "Open the Gig Board" }}
        busy={busy}
        onComplete={onComplete}
      />
    </div>
  );
}
